import { FC, useState } from "react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { CoverImage } from "@/components";
import Image from "@/assets/cover/news_dbg.webp";
import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { Collapsible } from "radix-ui";
import { useDownloadUrl } from "@/features/gameTracker/hooks/useDownloadUrl";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { Link } from "react-router-dom";

const GameTrackerPage: FC = (): JSX.Element => {
  // Page Document Title
  useDocumentTitle("Download vores Game Tracker");
  const [openCollapsible, setOpenCollapsible] = useState<number | null>(null);

  const handleOpenChange = (index: number) => {
    setOpenCollapsible(openCollapsible === index ? null : index);
  };

  const { downloadUrl, loading, error } = useDownloadUrl(brand.tenantId);

  return (
    <>
      <CoverImage
        src={Image}
        alt="CoverImage"
        heading="Download vores Game Tracker"
        desc={
          !error
            ? "Hent vores Game Tracker for at deltage i vores challenges."
            : error.message
        }
      >
        {downloadUrl && (
          <Button
            variant="solid"
            disabled={loading}
            onClick={() => (window.location.href = downloadUrl)}
          >
            Download nu
          </Button>
        )}
      </CoverImage>
      <Heading as="h2" size="5" align="center" mb="3">
        Ofte Stillede Spørgsmål
      </Heading>

      <Flex direction="column" gap="3">
        <Collapsible.Root
          open={openCollapsible === 0}
          onOpenChange={() => handleOpenChange(0)}
        >
          <Card>
            <Collapsible.Trigger asChild>
              <Text>Er Game Tracker sikkert at installere?</Text>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Text size="2" color="gray">
                Ja! Vores Game Tracker er udviklet med Electron.js og indeholder
                ingen malware. Da det er et relativt nyt program, kan nogle
                antivirusprogrammer fejlagtigt markere det som en trussel. Andre
                programmer, der er udviklet med Electron.js, inkluderer Discord,
                Slack og Visual Studio Code.
              </Text>
            </Collapsible.Content>
          </Card>
        </Collapsible.Root>
        <Collapsible.Root
          open={openCollapsible === 1}
          onOpenChange={() => handleOpenChange(1)}
        >
          <Card>
            <Collapsible.Trigger asChild>
              <Text>
                Hvorfor blokerer mit Antivirus installationen af Game Tracker?
              </Text>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Text size="2" color="gray">
                Avast og enkelte andre antivirusprogrammer markerer nogle gange
                nye programmer som potentielle trusler. Dette skyldes deres
                scanningsmetode, som forsøger at opdage ukendt eller mistænkelig
                adfærd, selvom programmet er sikkert.
              </Text>
            </Collapsible.Content>
          </Card>
        </Collapsible.Root>

        <Collapsible.Root
          open={openCollapsible === 2}
          onOpenChange={() => handleOpenChange(2)}
        >
          <Card>
            <Collapsible.Trigger asChild>
              <Text>
                Hvordan installerer jeg programmet, hvis mit antivirus blokerer
                det?
              </Text>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Text size="2" color="gray">
                Hvis dit antivirus forhindrer dig i at installere Game Tracker,
                kan du prøve at deaktiver dit antivirus midlertidigt, eller
                tilføje en undtagelse for installationsfilen.
              </Text>
            </Collapsible.Content>
          </Card>
        </Collapsible.Root>

        <Collapsible.Root
          open={openCollapsible === 3}
          onOpenChange={() => handleOpenChange(3)}
        >
          <Card>
            <Collapsible.Trigger asChild>
              <Text>Hvad gør jeg, hvis jeg stadig har problemer?</Text>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Text size="2">
                <Text color="gray">
                  Hvis dit antivirus stadig blokerer installationen, kan du
                  prøve at bruge et andet betroet antivirusprogram eller
                  kontakte vores supportteam via vores{" "}
                </Text>
                <Link to="/feedback">feedback formular på hjemmesiden</Link>.
              </Text>
            </Collapsible.Content>
          </Card>
        </Collapsible.Root>
      </Flex>
      <Heading as="h2" size="5" align="center" mt="9" mb="3">
        Forza Motorsport
      </Heading>
      <Flex direction="column" gap="3">
        <Collapsible.Root
          open={openCollapsible === 4}
          onOpenChange={() => handleOpenChange(4)}
        >
          <Card>
            <Collapsible.Trigger asChild>
              <Text>
                Hvordan slår jeg Telemetry Data til i Forza Motorsport?
              </Text>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Text as="div" size="2" color="gray">
                <ol>
                  <li>Åben Forza Motorsport 8 på din PC.</li>
                  <li>
                    Gå to Settings (Indstillinger), herefter Gameplay & HUD og
                    scroll ned til bunden af skærmen.
                  </li>
                  <li>Vælg Data Out til On.</li>
                  <li>Data Out IP Address skal være 127.0.0.1.</li>
                  <li>Data Out Port sættes til 9967.</li>
                  <li>Car Out Packet Format sættes til Car Dash.</li>
                </ol>
              </Text>
            </Collapsible.Content>
          </Card>
        </Collapsible.Root>
      </Flex>
    </>
  );
};

export default GameTrackerPage;
